import { fetchAuthSession, fetchMFAPreference } from 'aws-amplify/auth';
import { useQuery } from '@tanstack/react-query';

export const mfaPreferenceQueryKey = ['mfa-preference'];

/** `applicable: false` means this session cannot manage MFA factors at all. */
export type MfaEnrollment = { applicable: false } | { applicable: true; enabled: string[] };

type MfaProbe = { isSuccess: boolean; data?: MfaEnrollment };

/** Fail open: an unknown enrollment state must never gate the app. */
export function shouldPromptMfaSetup({ isSuccess, data }: MfaProbe): boolean {
  if (!isSuccess || !data?.applicable) return false;
  return data.enabled.length === 0;
}

// GetUser requires the aws.cognito.signin.user.admin scope. API sign-in carries it;
// hosted-UI (Google/Apple) tokens carry only the scopes the app client allows, and
// Cognito never applies user-pool MFA to federated sessions anyway.
export async function fetchMfaEnrollment(): Promise<MfaEnrollment> {
  const { tokens } = await fetchAuthSession();
  const scopes = String(tokens?.accessToken?.payload?.scope).split(' ');
  if (!scopes.includes('aws.cognito.signin.user.admin')) return { applicable: false };

  const { enabled } = await fetchMFAPreference();
  return { applicable: true, enabled: enabled ?? [] };
}

export function useMfaEnrollment({ enabled }: { enabled: boolean }) {
  const query = useQuery({
    queryKey: mfaPreferenceQueryKey,
    queryFn: fetchMfaEnrollment,
    enabled,
    // One retry recovers a network blip; more would hold the guard's spinner up longer
    // for a failure that fails open anyway.
    retry: 1,
    staleTime: Infinity,
  });

  return {
    isLoading: query.isLoading,
    shouldPromptSetup: shouldPromptMfaSetup(query),
  };
}
